import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, RefreshCw, Clock, Scale } from 'lucide-react';
import { usePage } from '../../hooks/usePage';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../hooks/useAuth';
import {
  businessReconciliationApi,
  ReconciliationActivity,
  ReconciliationSeverity,
  ReconciliationSummary,
  StuckTransfer,
} from '../../api/business-reconciliation';
// The ledger repairs live on the accounts page too. They belong here as
// well, because missing rows are what a drift is usually made of, and the
// place somebody discovers the gap should be the place they can close it.
import {
  businessAccountsApi,
  LedgerRepairView,
} from '../../api/business-accounts';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Drawer } from '../../components/ui/Drawer';
import { EmptyState } from '../../components/ui/EmptyState';
import { Spinner } from '../../components/ui/Spinner';
import { StatCard } from '../../components/ui/StatCard';

/**
 * Ledger versus bank, for every business account.
 *
 * The daily sweep has been running for a while, but its findings only ever
 * became a log line, an audit event and an alert — so the question "which
 * accounts are drifting right now?" had no answer short of grepping logs.
 * This is that answer.
 *
 * Nothing on this page changes a ledger, and nothing should. A correction
 * that matches our numbers to the bank's papers over whatever caused the
 * gap, and afterwards the fix is indistinguishable from the fault. The only
 * write here re-runs the check.
 */

const severityLabel: Record<ReconciliationSeverity, string> = {
  drift: 'Drift',
  unreachable: 'Unreachable',
  expected: 'In flight',
  clean: 'Clean',
};

const severityVariant: Record<
  ReconciliationSeverity,
  'danger' | 'warning' | 'info' | 'success'
> = {
  drift: 'danger',
  // Not "clean-ish". An account we could not check is an account we cannot
  // vouch for, and it sits second in the list for the same reason.
  unreachable: 'warning',
  expected: 'info',
  clean: 'success',
};

const naira = (n: number) =>
  `₦${n.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-NG', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const th: React.CSSProperties = {
  padding: '12px 14px',
  textAlign: 'left',
  fontSize: 12,
  color: 'var(--neutral-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const td: React.CSSProperties = { padding: '12px 14px', verticalAlign: 'top' };

const selectStyle: React.CSSProperties = {
  height: 38,
  padding: '0 32px 0 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-700)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  cursor: 'pointer',
};

export const BusinessReconciliation: React.FC = () => {
  usePage('Business Reconciliation');
  const toast = useToast();
  const { can } = useAuth();

  // Read from the URL so the drift alert's link lands on the filtered view.
  const [searchParams, setSearchParams] = useSearchParams();
  const severity = (searchParams.get('severity') ?? '') as
    | ReconciliationSeverity
    | '';

  const [rows, setRows] = useState<ReconciliationSummary[] | null>(null);
  const [stuck, setStuck] = useState<StuckTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  /**
   * The drawer is the whole point of this page now.
   *
   * The list says an account is drifting; the drawer is where somebody
   * actually does something about it. It carries the account's history (is
   * this new, or has it been wrong since Tuesday?), the ledger the drift
   * disagrees with, and the three actions that close a finding: re-check,
   * repair the ledger, acknowledge.
   */
  const [history, setHistory] = useState<{
    account: ReconciliationSummary;
    checks: ReconciliationSummary[] | null;
    activity: ReconciliationActivity[] | null;
  } | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<'recheck' | 'repair' | 'ack' | null>(null);
  const [repairs, setRepairs] = useState<LedgerRepairView[] | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [list, stuckTransfers] = await Promise.all([
        businessReconciliationApi.list({ severity: severity || undefined }),
        businessReconciliationApi.stuck(),
      ]);
      setRows(list);
      setStuck(stuckTransfers);
    } catch (err) {
      toast.error(err, 'Failed to load reconciliation.');
    } finally {
      setLoading(false);
    }
  }, [severity, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  async function runSweep() {
    try {
      setRunning(true);
      const result = await businessReconciliationApi.run();
      toast.success(
        `Checked ${result.checked} account${result.checked === 1 ? '' : 's'} — ` +
          `${result.drifting} drifting, ${result.unreachable} unreachable.`,
      );
      await load();
    } catch (err) {
      toast.error(err, 'Could not run the sweep.');
    } finally {
      setRunning(false);
    }
  }

  async function openHistory(row: ReconciliationSummary) {
    setHistory({ account: row, checks: null, activity: null });
    setNote('');
    setRepairs(null);
    try {
      // The three reads the drawer needs, together. Loading them in sequence
      // makes the panel fill in three visible steps for no benefit.
      const [checks, activity, ledger] = await Promise.all([
        businessReconciliationApi.history(row.accountId),
        businessReconciliationApi.activity(row.accountId),
        // Outstanding repairs are estate-wide rather than per-account, and
        // that is honest: the fixes are idempotent and global. It is still
        // the right thing to show here, because missing rows are what drift
        // is usually made of.
        businessAccountsApi.previewLedger().catch(() => null),
      ]);
      setHistory({ account: row, checks, activity });
      setRepairs(ledger?.repairs ?? []);
    } catch (err) {
      toast.error(err, 'Could not load this account\u2019s detail.');
      setHistory(null);
    }
  }

  /** Re-check this one account, and show what it now says. */
  async function recheck() {
    if (!history) return;
    setBusy('recheck');
    try {
      const result = await businessReconciliationApi.recheck(
        history.account.accountId,
      );
      toast.success(result.explanation);
      await openHistory(history.account);
      await load();
    } catch (err) {
      toast.error(err, 'Could not re-check that account.');
    } finally {
      setBusy(null);
    }
  }

  /**
   * Write the ledger rows that were never written.
   *
   * The nearest thing to a real fix on this page — and note what it is not:
   * it inserts the VAT, stamp-duty and reversal rows that a transfer should
   * have produced, rather than adjusting a balance to match the bank. The
   * difference matters. One reconstructs what happened; the other hides that
   * anything did.
   */
  async function repairLedger() {
    setBusy('repair');
    try {
      const report = await businessAccountsApi.repairLedger();
      toast.success(
        report.repaired === 0
          ? 'Nothing to repair — the missing rows were already written.'
          : `Wrote ${report.repaired} missing ledger row${report.repaired === 1 ? '' : 's'}. Re-check to see if that closed the gap.`,
      );
      setRepairs(report.repairs);
    } catch (err) {
      toast.error(err, 'Could not run the ledger repairs.');
    } finally {
      setBusy(null);
    }
  }

  /** Record that this drift is understood. Never changes the figure. */
  async function acknowledge() {
    if (!history) return;
    setBusy('ack');
    try {
      await businessReconciliationApi.acknowledge(history.account.id, note.trim());
      toast.success('Recorded. This drift will stop reading as unresolved.');
      setNote('');
      await openHistory(history.account);
      await load();
    } catch (err) {
      toast.error(err, 'Could not save that note.');
    } finally {
      setBusy(null);
    }
  }

  // Counted from the unfiltered set only — with a filter applied these would
  // describe the filter rather than the estate, and "0 drifting" while
  // looking at the clean-only view is a genuinely dangerous thing to show.
  const totals = severity
    ? null
    : {
        drifting: rows?.filter((r) => r.severity === 'drift').length ?? 0,
        unreachable: rows?.filter((r) => r.severity === 'unreachable').length ?? 0,
        checked: rows?.length ?? 0,
      };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card padding={20}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 16,
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: 'var(--neutral-600)',
              maxWidth: 680,
            }}
          >
            Every business account, compared against what Safe Haven says it
            holds. The sweep runs at 03:00 Lagos daily. A balance below the
            ledger is only a problem if it&apos;s below what&apos;s currently
            in flight — anything inside that band is expected. Nothing here
            corrects a ledger; a drift is a question for a person.
          </p>
          {can('business_reconciliation.run') && (
            <Button
              variant="secondary"
              onClick={() => void runSweep()}
              disabled={running}
            >
              <RefreshCw size={15} style={{ marginRight: 6 }} />
              {running ? 'Checking…' : 'Run now'}
            </Button>
          )}
        </div>

        {totals && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
              marginBottom: 20,
            }}
          >
            <StatCard
              label="Drifting"
              value={totals.drifting}
              icon={AlertTriangle}
              accent={totals.drifting > 0 ? 'purple' : 'neutral'}
            />
            <StatCard
              label="Unreachable"
              value={totals.unreachable}
              icon={Scale}
              accent="neutral"
            />
            <StatCard
              label="Accounts checked"
              value={totals.checked}
              accent="blue"
            />
            <StatCard
              label="Transfers stuck over an hour"
              value={stuck.length}
              icon={Clock}
              accent={stuck.length > 0 ? 'purple' : 'neutral'}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <select
            value={severity}
            onChange={(e) => {
              const next = e.target.value;
              setSearchParams(next ? { severity: next } : {});
            }}
            style={selectStyle}
          >
            <option value="">All accounts</option>
            <option value="drift">Drifting</option>
            <option value="unreachable">Unreachable</option>
            <option value="expected">In flight</option>
            <option value="clean">Clean</option>
          </select>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : !rows?.length ? (
          <EmptyState
            title={
              severity
                ? 'No accounts match this filter.'
                : 'No checks recorded yet.'
            }
            description={
              severity
                ? undefined
                : 'The sweep runs at 03:00 Lagos. Run it now to see where things stand.'
            }
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                minWidth: 900,
                borderCollapse: 'collapse',
                fontSize: 13,
              }}
            >
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {[
                    'Account',
                    'Status',
                    'Drift',
                    'Ledger',
                    'Bank',
                    'Checked',
                    '',
                  ].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={td}>
                      <div style={{ fontWeight: 600, color: 'var(--neutral-900)' }}>
                        {r.accountNumber}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                        {r.explanation}
                      </div>
                    </td>
                    <td style={td}>
                      <Badge variant={severityVariant[r.severity]}>
                        {severityLabel[r.severity]}
                      </Badge>
                    </td>
                    <td
                      style={{
                        ...td,
                        fontWeight: 600,
                        color:
                          r.severity === 'drift'
                            ? 'var(--danger-900)'
                            : 'var(--neutral-700)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.severity === 'unreachable' ? '—' : naira(r.driftNaira)}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {naira(r.ledgerSettledNaira)}
                      {r.ledgerInFlightNaira > 0 && (
                        <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                          {naira(r.ledgerInFlightNaira)} in flight
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {/* Em dash, not ₦0.00. We don't know this number, and
                          showing a zero would claim the account is empty. */}
                      {r.providerBalanceNaira === null
                        ? '—'
                        : naira(r.providerBalanceNaira)}
                    </td>
                    <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                      {dateTime(r.checkedAt)}
                    </td>
                    <td style={td}>
                      <Button variant="ghost" onClick={() => void openHistory(r)}>
                        History
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {stuck.length > 0 && (
        <Card padding={20}>
          <h3
            style={{
              margin: '0 0 6px',
              fontSize: 15,
              color: 'var(--neutral-900)',
            }}
          >
            Transfers that never settled
          </h3>
          <p
            style={{
              margin: '0 0 16px',
              fontSize: 13,
              color: 'var(--neutral-600)',
              maxWidth: 680,
            }}
          >
            Handed to Safe Haven over an hour ago and still pending. Named,
            never resolved automatically — Safe Haven has no requery API, so
            assuming one of these succeeded is how a payment gets sent twice.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', fontSize: 13 }}
            >
              <thead>
                <tr style={{ background: 'var(--neutral-200)' }}>
                  {['Reference', 'Amount', 'To', 'Sent'].map((h) => (
                    <th key={h} style={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stuck.map((t) => (
                  <tr key={t.id} style={{ borderTop: '1px solid var(--neutral-300)' }}>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>
                      {t.reference}
                    </td>
                    <td style={{ ...td, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {naira(t.amount)}
                    </td>
                    <td style={td}>
                      {t.accountNumber}
                      <span style={{ color: 'var(--neutral-500)' }}> · {t.bankCode}</span>
                    </td>
                    <td style={{ ...td, color: 'var(--neutral-500)', whiteSpace: 'nowrap' }}>
                      {dateTime(t.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Drawer
        isOpen={history !== null}
        onClose={() => setHistory(null)}
        title={history?.account.accountNumber ?? ''}
        subtitle="What the gap is, and what to do about it"
      >
        {history?.checks && history.account.severity !== 'clean' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
            {/* ── What the gap is ─────────────────────────────────────── */}
            <div
              style={{
                border: '1px solid var(--neutral-300)',
                borderRadius: 'var(--radius-md)',
                padding: 14,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
                {history.account.explanation}
              </div>
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
                <span style={{ color: 'var(--neutral-500)' }}>
                  Ledger{' '}
                  <strong style={{ color: 'var(--neutral-800)' }}>
                    {naira(history.account.ledgerSettledNaira)}
                  </strong>
                </span>
                <span style={{ color: 'var(--neutral-500)' }}>
                  Bank{' '}
                  <strong style={{ color: 'var(--neutral-800)' }}>
                    {history.account.providerBalanceNaira === null
                      ? 'unknown'
                      : naira(history.account.providerBalanceNaira)}
                  </strong>
                </span>
                <span style={{ color: 'var(--neutral-500)' }}>
                  Difference{' '}
                  <strong style={{ color: 'var(--danger-600, #b42318)' }}>
                    {naira(history.account.driftNaira)}
                  </strong>
                </span>
              </div>
            </div>

            {/* ── Already accounted for ───────────────────────────────── */}
            {history.account.acknowledgedAt ? (
              <div
                style={{
                  border: '1px solid var(--neutral-300)',
                  background: 'var(--neutral-100)',
                  borderRadius: 'var(--radius-md)',
                  padding: 14,
                  fontSize: 13,
                  color: 'var(--neutral-700)',
                }}
              >
                {/* The drift is still on the row at its real figure. This is
                    evidence beside the discrepancy, not in place of it. */}
                <strong>Accounted for</strong> on{' '}
                {dateTime(history.account.acknowledgedAt)} —{' '}
                {history.account.acknowledgementNote}
              </div>
            ) : null}

            {/* ── What can actually be done ───────────────────────────── */}
            {can('business_reconciliation.run') ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button
                    variant="secondary"
                    disabled={busy !== null}
                    onClick={() => void recheck()}
                  >
                    {busy === 'recheck' ? 'Re-checking…' : 'Re-check this account'}
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={busy !== null}
                    onClick={() => void repairLedger()}
                  >
                    {busy === 'repair' ? 'Repairing…' : 'Write missing ledger rows'}
                  </Button>
                </div>

                {/* Missing rows are what drift is usually made of, so the
                    outstanding counts belong next to the gap they explain.
                    The repairs are estate-wide and idempotent — that is
                    stated rather than hidden behind a per-account label the
                    backend could not honour. */}
                {repairs && repairs.some((r) => r.outstanding > 0) ? (
                  <div style={{ fontSize: 12, color: 'var(--neutral-600)', lineHeight: 1.6 }}>
                    Outstanding across all businesses:{' '}
                    {repairs
                      .filter((r) => r.outstanding > 0)
                      .map((r) => `${r.label} (${r.outstanding})`)
                      .join(' · ')}
                  </div>
                ) : repairs ? (
                  <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    No missing ledger rows anywhere — this gap is something
                    else.
                  </div>
                ) : null}

                {!history.account.acknowledgedAt ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <label style={{ fontSize: 12, color: 'var(--neutral-600)' }}>
                      Not a bug? Say what explains it — this is recorded
                      against your name and never changes the figure.
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={2}
                      placeholder="e.g. Safe Haven card-scheme fee we don't model yet"
                      style={{
                        width: '100%',
                        padding: 10,
                        fontSize: 13,
                        fontFamily: 'var(--font-family)',
                        color: 'var(--neutral-800)',
                        border: '1px solid var(--neutral-300)',
                        borderRadius: 'var(--radius-md)',
                        resize: 'vertical',
                      }}
                    />
                    <div>
                      <Button
                        variant="secondary"
                        disabled={busy !== null || note.trim().length < 4}
                        onClick={() => void acknowledge()}
                      >
                        {busy === 'ack' ? 'Saving…' : 'Mark as accounted for'}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* ── The ledger the drift disagrees with ─────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--neutral-600)' }}>
                Recent ledger activity
              </div>
              {!history.activity ? (
                <Spinner />
              ) : history.activity.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--neutral-500)' }}>
                  Nothing on this business&apos;s ledger yet — which is itself
                  worth knowing when the bank says otherwise.
                </div>
              ) : (
                history.activity.map((t) => (
                  <div
                    key={t.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      fontSize: 12,
                      padding: '8px 0',
                      borderBottom: '1px solid var(--neutral-200)',
                    }}
                  >
                    <span style={{ color: 'var(--neutral-700)' }}>
                      {t.counterparty ?? t.narration ?? t.reference}
                      <span style={{ color: 'var(--neutral-500)' }}>
                        {' '}· {t.category} · {t.status}
                      </span>
                    </span>
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        color:
                          t.type === 'credit'
                            ? 'var(--success-600, #067647)'
                            : 'var(--neutral-800)',
                      }}
                    >
                      {t.type === 'credit' ? '+' : '−'}
                      {naira(t.amount)}
                    </span>
                  </div>
                ))
              )}
            </div>

            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--neutral-600)' }}>
              Every check, newest first
            </div>
          </div>
        ) : null}

        {/* The question a single row can't answer: is this new, or has it
            been wrong since Tuesday? */}
        {!history?.checks ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spinner />
          </div>
        ) : history.checks.length === 0 ? (
          <EmptyState title="No checks recorded for this account yet." />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {history.checks.map((c) => (
              <div
                key={c.id}
                style={{
                  border: '1px solid var(--neutral-300)',
                  borderRadius: 'var(--radius-md)',
                  padding: 14,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <Badge variant={severityVariant[c.severity]}>
                    {severityLabel[c.severity]}
                  </Badge>
                  <span style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                    {dateTime(c.checkedAt)}
                  </span>
                </div>
                <div style={{ fontSize: 13, color: 'var(--neutral-700)' }}>
                  {c.explanation}
                </div>
                <div style={{ fontSize: 12, color: 'var(--neutral-500)' }}>
                  Ledger {naira(c.ledgerSettledNaira)} · Bank{' '}
                  {c.providerBalanceNaira === null
                    ? 'unknown'
                    : naira(c.providerBalanceNaira)}
                </div>
              </div>
            ))}
          </div>
        )}
      </Drawer>
    </div>
  );
};
